/**
 * Safari < 14 (macOS 10.15 / iOS 13 and older) implements `MediaQueryList` with only the
 * deprecated `addListener`/`removeListener` pair — the `EventTarget` methods are missing.
 *
 * MUI dropped its own fallback in v6, so `useMediaQuery` calls
 * `queryList.addEventListener('change', …)` unconditionally and throws
 * `TypeError: l.addEventListener is not a function` on those browsers. The throw happens in an
 * effect, so React tears the tree down and the Bugsnag error boundary replaces the page.
 *
 * This shim wraps `window.matchMedia` so every `MediaQueryList` it hands out carries the modern
 * pair, delegating to the legacy one. Wrapping the factory rather than patching
 * `MediaQueryList.prototype` keeps us off a shared global prototype and works regardless of how
 * the host produces the objects.
 *
 * Only the `change` event exists on the legacy API, so other event types are ignored, as are the
 * `options` argument (`capture`/`once`/`signal`) and `AbortSignal`-based removal.
 */

let isInstalled = false;

/**
 * Legacy `removeListener` matches on function identity, so an `EventListenerObject` has to
 * resolve to the same wrapper it was added with. One wrapper per listener object is enough:
 * removal is scoped to the `MediaQueryList` it is called on, not to the wrapper.
 */
const objectListenerHandlers = new WeakMap<
	EventListenerObject,
	(event: MediaQueryListEvent) => void
>();

type LegacyMediaQueryList = MediaQueryList & {
	addListener?: (listener: (event: MediaQueryListEvent) => void) => void;
	removeListener?: (listener: (event: MediaQueryListEvent) => void) => void;
};

const resolveHandler = (
	listener: EventListenerOrEventListenerObject
): ((event: MediaQueryListEvent) => void) => {
	if (typeof listener === 'function') {
		return listener as (event: MediaQueryListEvent) => void;
	}

	const existing = objectListenerHandlers.get(listener);
	if (existing) {
		return existing;
	}

	const handler = (event: MediaQueryListEvent) => listener.handleEvent(event);
	objectListenerHandlers.set(listener, handler);
	return handler;
};

const patchMediaQueryList = (mediaQueryList: LegacyMediaQueryList): MediaQueryList => {
	const { addListener, removeListener } = mediaQueryList;

	// Nothing to do on a modern browser, and nothing we *can* do without the legacy pair.
	if (typeof addListener !== 'function' || typeof removeListener !== 'function') {
		return mediaQueryList;
	}

	if (typeof mediaQueryList.addEventListener !== 'function') {
		mediaQueryList.addEventListener = ((
			type: string,
			listener: EventListenerOrEventListenerObject | null
		) => {
			if (type !== 'change' || !listener) {
				return;
			}
			addListener.call(mediaQueryList, resolveHandler(listener));
		}) as MediaQueryList['addEventListener'];
	}

	if (typeof mediaQueryList.removeEventListener !== 'function') {
		mediaQueryList.removeEventListener = ((
			type: string,
			listener: EventListenerOrEventListenerObject | null
		) => {
			if (type !== 'change' || !listener) {
				return;
			}
			removeListener.call(mediaQueryList, resolveHandler(listener));
		}) as MediaQueryList['removeEventListener'];
	}

	return mediaQueryList;
};

/**
 * Must run before the first React render — MUI reads `window.matchMedia` on every `useMediaQuery`
 * call, so wrapping it at startup covers our own hooks and MUI's internal ones alike.
 */
export const installMatchMediaEventTargetPolyfill = () => {
	if (isInstalled || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
		return;
	}

	isInstalled = true;

	const nativeMatchMedia = window.matchMedia.bind(window);

	window.matchMedia = (query: string): MediaQueryList =>
		patchMediaQueryList(nativeMatchMedia(query) as LegacyMediaQueryList);
};
